import { useState, useEffect } from "react";
import { CircularProgress, Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import { AxiosError } from "axios";
import {
  Organization,
  getOrganizations,
  getOrganizationUsers,
  getOrganizationBrands,
  deleteOrganization,
} from "../../api/organization";
import { OrganizationList } from "./organization-list";
import { OrganizationDialog } from "./organization-dialog";
import { OrganizationDetailView } from "./organization-detail-view";
import { GlobalUsageDialog } from "./global-usage-dialog";

type View = "list" | "detail";

export default function OrganizationManagement() {
  const [isLoading, setIsLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationUserCounts, setOrganizationUserCounts] = useState<
    Record<string, number>
  >({});
  const [organizationBrandCounts, setOrganizationBrandCounts] = useState<
    Record<string, number>
  >({});

  // View state
  const [currentView, setCurrentView] = useState<View>("list");
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  // Dialog states
  const [openOrganizationDialog, setOpenOrganizationDialog] = useState(false);
  const [dialogOrganization, setDialogOrganization] =
    useState<Organization | null>(null);

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteOrganizationId, setDeleteOrganizationId] = useState<
    string | null
  >(null);
  const [openGlobalUsage, setOpenGlobalUsage] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch organizations
      const orgsResponse = await getOrganizations();
      const orgsData = orgsResponse?.data?.data?.items || [];
      setOrganizations(orgsData);

      // Fetch user and brand counts for each organization
      const userCounts: Record<string, number> = {};
      const brandCounts: Record<string, number> = {};

      for (const org of orgsData) {
        try {
          const usersResponse = await getOrganizationUsers(org.id);
          const users = usersResponse?.data?.data || [];
          userCounts[org.id] = Array.isArray(users) ? users.length : 0;

          const brandsResponse = await getOrganizationBrands(org.id);
          const brands = brandsResponse?.data?.data || [];
          brandCounts[org.id] = Array.isArray(brands) ? brands.length : 0;
        } catch (error) {
          console.error(`Error fetching counts for org ${org.id}:`, error);
          userCounts[org.id] = 0;
          brandCounts[org.id] = 0;
        }
      }

      setOrganizationUserCounts(userCounts);
      setOrganizationBrandCounts(brandCounts);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update selectedOrganization when organizations change (after edit)
  useEffect(() => {
    if (selectedOrganization && organizations.length > 0) {
      const updatedOrg = organizations.find(
        (org) => org.id === selectedOrganization.id
      );
      if (updatedOrg) {
        setSelectedOrganization(updatedOrg);
      }
    }
  }, [organizations]);

  const handleCreateOrganization = () => {
    setDialogOrganization(null);
    setOpenOrganizationDialog(true);
  };

  const handleEditOrganization = (organization: Organization) => {
    setDialogOrganization(organization);
    setOpenOrganizationDialog(true);
  };

  const handleViewDetails = (organization: Organization) => {
    setSelectedOrganization(organization);
    setCurrentView("detail");
  };

  const handleBackToList = () => {
    setCurrentView("list");
    setSelectedOrganization(null);
  };

  const handleDeleteOrganization = (organizationId: string) => {
    setDeleteOrganizationId(organizationId);
    setOpenDeleteConfirm(true);
  };

  const handleDisableEnableOrganization = async (
    organizationId: string,
    newDisabledState: boolean
  ) => {
    // Update the organization in state immediately for optimistic UI
    setOrganizations((prev) =>
      prev.map((org) =>
        org.id === organizationId ? { ...org, disabled: newDisabledState } : org
      )
    );

    // Refresh data to ensure consistency
    fetchData();
  };

  const confirmDelete = async () => {
    if (!deleteOrganizationId) return;

    try {
      await deleteOrganization(deleteOrganizationId);
      setOpenDeleteConfirm(false);
      setOpenOrganizationDialog(false);
      setCurrentView("list");
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error deleting organization:", error);

      let errorMessage = "Failed to delete organization. It may still have users or brands associated.";

      if (error instanceof AxiosError && error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      alert(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {currentView === "list" ? (
        <OrganizationList
          organizations={organizations}
          organizationUserCounts={organizationUserCounts}
          organizationBrandCounts={organizationBrandCounts}
          onCreateOrganization={handleCreateOrganization}
          onEditOrganization={handleEditOrganization}
          onViewDetails={handleViewDetails}
          onShowGlobalUsage={() => setOpenGlobalUsage(true)}
        />
      ) : (
        selectedOrganization && (
          <OrganizationDetailView
            organization={selectedOrganization}
            onBack={handleBackToList}
            onEdit={() => {
              handleEditOrganization(selectedOrganization);
            }}
            onDelete={handleDeleteOrganization}
            onRefresh={fetchData}
          />
        )
      )}

      <OrganizationDialog
        open={openOrganizationDialog}
        organization={dialogOrganization}
        onClose={(refresh) => {
          setOpenOrganizationDialog(false);
          if (refresh) fetchData();
        }}
        onDelete={handleDeleteOrganization}
        onDisableEnable={handleDisableEnableOrganization}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
      >
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this organization? This action
            cannot be undone. The organization must have no users or brands
            associated with it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global Usage Dialog */}
      <GlobalUsageDialog
        open={openGlobalUsage}
        onClose={() => setOpenGlobalUsage(false)}
      />
    </>
  );
}
